import { useState } from 'react'
import { Modal } from './Modal'
import { PhoneMultiSelect } from './PhoneMultiSelect'
import { ShowKeyOnce } from './ShowKeyOnce'
import { useCreateAccessKey, usePhones } from '../api'

const LABEL_MAX = 100

interface CreateAccessKeyDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Two-phase dialog: form → plaintext-reveal.
 *
 * After successful create the form closes and `ShowKeyOnce` opens with the
 * new key. Closing the reveal runs `onClose`, which bubbles up to the page
 * and refetches the access-key list (via `useCreateAccessKey` onSuccess
 * invalidation).
 */
export function CreateAccessKeyDialog({ open, onClose }: CreateAccessKeyDialogProps) {
  const { data: phones = [] } = usePhones()
  const createMutation = useCreateAccessKey()
  const [label, setLabel] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  const reset = () => {
    setLabel('')
    setSelected([])
    setCreatedKey(null)
    createMutation.reset()
  }

  const close = () => {
    reset()
    onClose()
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selected.length === 0 || label.length > LABEL_MAX) return
    createMutation.mutate(
      { label: label.trim() || null, phone_ids: selected },
      { onSuccess: data => setCreatedKey(data.key) },
    )
  }

  const labelTooLong = label.length > LABEL_MAX
  const submitDisabled =
    createMutation.isPending || selected.length === 0 || labelTooLong

  // Phase 2 — reveal the plaintext key exactly once.
  if (createdKey) {
    return (
      <ShowKeyOnce
        open={open}
        plaintext={createdKey}
        title="Ключ создан"
        subtitle="Передайте ключ оператору. Он будет показан только сейчас."
        onClose={close}
      />
    )
  }

  // Phase 1 — form.
  return (
    <Modal open={open} onClose={close} title="Новый ключ доступа">
      <form onSubmit={submit} className="space-y-4">
        {createMutation.error && (
          <p className="text-red-500 text-sm">{createMutation.error.message}</p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Метка <span className="text-gray-400">(необязательно)</span>
          </label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Например, «Иван — доставка»"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {labelTooLong && (
            <p className="text-xs text-red-500 mt-1">
              Метка должна быть не длиннее {LABEL_MAX} символов
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Трубки <span className="text-red-500">*</span>
          </label>
          <PhoneMultiSelect
            phones={phones}
            selected={selected}
            onChange={setSelected}
            disabled={createMutation.isPending}
          />
          {selected.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Выберите минимум одну свободную трубку
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={submitDisabled}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
          >
            {createMutation.isPending ? 'Создаю…' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
